import useSWR from 'swr';
import { useMemo, useCallback } from 'react';

import axiosInstance, { fetcher, endpoints } from 'src/lib/axios';
import { useAppContext } from 'src/context/app';

// ----------------------------------------------------------------------

/** Konstantnamnet i backends KontaktTyp. */
export type KontaktTyp = 'ANVÄNDARE' | 'HEM' | 'ORGANISATION';

export type Kontakt = {
  /** Null för sökträffar som ännu inte är sparade som kontakt. */
  id: string | null;
  typ: KontaktTyp;
  typEtikett: string;
  /** Iconify-namn, valt i backend så att nya typer inte kräver någon ändring här. */
  ikon: string;
  /** Användar-, kartpunkts- eller org-id beroende på typ. */
  målId: string;
  visningsnamn: string;
  underrubrik: string | null;
  avatarUrl: string | null;
  /** Bara satt för personer, och avsett för kontaktkortet — inte för listraden. */
  epost: string | null;
  telefon: string | null;
  länk: string | null;
  kanSkickaMeddelande: boolean;
  fäst: boolean;
  senasteKontakt: string | null;
  antalKontakter: number;
};

type KontakterSvar = {
  kontakter: Kontakt[];
  antal: number;
};

const TOMT: KontakterSvar = { kontakter: [], antal: 0 };

/**
 * Till skillnad från övriga actions används inte det delade swrOptions här — det stänger av
 * all revalidering, och listan ska uppdateras när man varit och skrivit någonstans.
 */
const swrOptions = {
  revalidateOnFocus: true,
  keepPreviousData: true,
};

export function useKontakter() {
  // Kontakterna är föreningens och hämtas via `X-Organisation-Id`. Utan förening
  // (Privatperson) skickas inget huvud, och anropet kan bara sluta med fel — så
  // det görs inte alls.
  const { organisation } = useAppContext();

  const { data, isLoading, error, mutate } = useSWR<KontakterSvar>(
    organisation ? endpoints.kontakter.lista : null,
    fetcher,
    swrOptions
  );

  const svar = data ?? TOMT;

  const läggTill = useCallback(
    async (typ: KontaktTyp, målId: string) => {
      await axiosInstance.post(endpoints.kontakter.läggTill, { typ, målId });
      await mutate();
    },
    [mutate]
  );

  const dölj = useCallback(
    async (id: string) => {
      await axiosInstance.delete(endpoints.kontakter.dölj(id));
      await mutate();
    },
    [mutate]
  );

  const växlaFäst = useCallback(
    async (id: string) => {
      await axiosInstance.post(endpoints.kontakter.fäst(id));
      await mutate();
    },
    [mutate]
  );

  return useMemo(
    () => ({
      kontakter: svar.kontakter,
      antal: svar.antal,
      kontakterLoading: isLoading,
      kontakterError: error,
      kontakterEmpty: !isLoading && svar.kontakter.length === 0,
      läggTill,
      dölj,
      växlaFäst,
      uppdatera: mutate,
    }),
    [svar, isLoading, error, läggTill, dölj, växlaFäst, mutate]
  );
}

// ----------------------------------------------------------------------

/** Kandidater att lägga till. Hämtas först när dialogen öppnas. */
export function useKontaktKandidater(aktiv: boolean, fråga: string) {
  const url = aktiv
    ? `${endpoints.kontakter.sökbara}?q=${encodeURIComponent(fråga)}`
    : null;

  const { data, isLoading } = useSWR<Kontakt[]>(url, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  return useMemo(
    () => ({ kandidater: data ?? [], kandidaterLoading: isLoading }),
    [data, isLoading]
  );
}
